export enum UserGroupRolesEnum {
  STUDENT_ROLES = 1,
  AFFILIATION_ADMIN_ROLES = 2,
  TEACHER_ROLES = 3,
  INTERNAL_ADMIN_ROLES = 4,
  AFFILIATION_MANAGER_ROLES = 5,
  INTERNAL_CONTENT_ROLES = 6,
  TUTORING_ADMIN_ROLES = 7,
  TUTOR_ROLES = 8,
  TUTORING_STUDENT_ROLES = 9,
}

export enum StudentRoleIdsEnum {
  STUDENT = 11, // was 2
  DEMO_STUDENT = 12, // was 8
  DEACTIVATED_STUDENT = 13, // was 9
  PARENT = 14, // was 18
  OLYMPIAD_STUDENT = 15, // was 23
  STEMLEARN_STUDENT = 16, // was 24
  B2C_STUDENT = 17, // new
}

export enum AffiliationAdminRoleIdsEnum {
  CENTER_MANAGER = 21, // was 3
  B2C_INDIVIDUAL_TEACHER = 22, // was 19
  PRINCIPAL = 23, // new
  KDM = 24, // new
  SPOC = 25, // new
  CSM = 26, // new
  AFFILIATION_HEAD = 27, // was 7
}

export enum TeacherRoleIdsEnum {
  SCHOOL_TEACHER = 31, // was 20
}

export enum InternalAdminRoleIdsEnum {
  ADMIN = 41, // was 1
}

export enum AffiliationManagerRoleIdsEnum {
  AFFILIATION_MANAGER = 51, // was 21 (renamed from ACADEMIC_EXCELLENCE_MANAGER)
  SCHOOL_PARTNER_RM = 52, // was 22
}

// Group 6 — Internal content roles
export enum InternalContentRoleIdsEnum {
  CONTENT_MANAGER = 61, // was 14
  FACULTY = 62, // was 4
}

export enum TutoringAdminRoleIdsEnum {
  TUTORING_ADMIN = 71, // was 13
  TUTORING_OPS = 72, // was 16
  TUTORING_SALES = 73, // was 17
}

export enum TutorRoleIdsEnum {
  TUTOR = 81, // was 11
}

export enum TutoringStudentRoleIdsEnum {
  TUTORING_STUDENT = 91, // was 6
}

export const AllUserRoleIdsEnum = {
  ...StudentRoleIdsEnum,
  ...AffiliationAdminRoleIdsEnum,
  ...TeacherRoleIdsEnum,
  ...InternalAdminRoleIdsEnum,
  ...AffiliationManagerRoleIdsEnum,
  ...InternalContentRoleIdsEnum,
  ...TutoringAdminRoleIdsEnum,
  ...TutorRoleIdsEnum,
  ...TutoringStudentRoleIdsEnum,
} as const;

export enum UserGroupRoleNamesEnum {
  STUDENT_ROLES = "Student Roles",
  AFFILIATION_ADMIN_ROLES = "Affiliation Admin Roles",
  TEACHER_ROLES = "Teacher Roles",
  INTERNAL_ADMIN_ROLES = "Internal Admin Roles",
  AFFILIATION_MANAGER_ROLES = "Affiliation Manager Roles",
  INTERNAL_CONTENT_ROLES = "Internal Content Roles",
  TUTORING_ADMIN_ROLES = "Tutoring Admin Roles",
  TUTOR_ROLES = "Tutor Roles",
  TUTORING_STUDENT_ROLES = "Tutoring Student Roles",
}

export enum GrantReasonIdsEnum {
  COIN_UNLOCK = 1,
  PURCHASED = 2,
}

export enum GrantReasonNamesEnum {
  COIN_UNLOCK = "Coin Unlock",
  PURCHASED = "Purchased",
}

// A view belongs to exactly ONE parent: the backend rejects (417) any role menu that hangs a view
// off a parent other than the one recorded in its NavbarViewConfig.ParentName. So this list is a
// closed set of groups, not per-role labels — adding a member here without a matching view config
// gets the same 417.
//
// REPORTS was consolidated into REVIEW: the two held overlapping screens (SchoolImplementationScore
// sat in both), which the one-parent rule makes impossible. REVIEW keeps the reporting glyph.
export enum NavbarParentNamesEnum {
  CONTENT = "Content",
  HOME = "Home",
  LEARN = "Learn",
  MANAGE = "Manage",
  PRACTICE = "Practice",
  PROFILE = "Profile",
  QUESTIONS = "Questions",
  REVIEW = "Review",
  SESSIONS = "Sessions",
  TEACHING = "Teaching",
  TEST = "Test",
}

export enum ViewNamesEnum {
  ADMIN_DASHBOARD = "AdminDashboard",
  ADMIN_MANAGE_AFFILIATIONS = "AdminManageAffiliations",
  AEM_MY_SCHOOLS = "AemMySchools",
  AEM_PRINCIPAL_ONBOARDING = "AemPrincipalOnboarding",
  AEM_TICKETS = "AemTickets",
  AFFILIATION_DASHBOARD = "AffiliationDashboard",
  AFFILIATION_PROFILE = "AffiliationProfile",
  AFFILIATION_TEACHER_MAPPING = "AffiliationTeacherMapping",
  ASSIGNMENTS = "Assignments",
  BATCH_TEACHER_MAPPING = "BatchTeacherMapping",
  CHANGE_PASSWORD = "ChangePassword",
  CHAPTER_QUESTIONS_REVIEW = "ChapterQuestionsReview",
  CHAPTER_WORKBENCH = "ChapterWorkbench",
  CONTENT_COURSE_LIBRARY = "ContentCourseLibrary",
  CONTENT_DESIGN_LIBRARY = "ContentDesignLibrary",
  CONTENT_LECTURE_LIBRARY = "ContentLectureLibrary",
  CONTENT_TEACHING_AID_LIBRARY = "ContentTeachingAidLibrary",
  CONTENT_VIDEO_LIBRARY = "ContentVideoLibrary",
  COURSE_WISE_QUESTION_COUNTS = "CourseWiseQuestionCounts",
  EVALUATE_SUBJECTIVE_QUESTIONS = "EvaluateSubjectiveQuestions",
  FIX_QUESTION = "FixQuestion",
  FIX_CHAPTER_QUESTIONS = "FixChapterQuestions",
  IMPLEMENTATION_SCORE = "ImplementationScore",
  INSTITUTE_TESTS = "InstituteTests",
  INSTITUTE_TEST_OFFLINE = "InstituteTestOffline",
  LEARN_CONCEPTS_VIDEOS = "LearnConceptsVideos",
  LEARN_DOUBTS_REVISION = "LearnDoubtsRevision",
  LIVE_CLASSES = "LiveClasses",
  LOGOUT = "Logout",
  MANAGE_ASSIGNMENTS = "ManageAssignments",
  MANAGE_BATCHES = "ManageBatches",
  MANAGE_INSTITUTE_TESTS = "ManageInstituteTests",
  MANAGE_LIVE_CLASSES = "ManageLiveClasses",
  MANAGE_OFFLINE_TESTS = "ManageOfflineTests",
  MANAGE_PAST_YEAR_PAPERS = "ManagePastYearPapers",
  MANAGE_PREDESIGNED_ACTIVITIES = "ManagePredesignedActivities",
  MANAGE_QR_CODES = "ManageQrCodes",
  MANAGE_QUESTION_PDFS = "ManageQuestionPdfs",
  MANAGE_SMART_BOOKS = "ManageSmartBooks",
  MANAGE_TEST_SERIES = "ManageTestSeries",
  MANAGE_USERS = "ManageUsers",
  PAST_YEAR_PAPERS = "PastYearPapers",
  PUBLISH_TEST_SERIES = "PublishTestSeries",
  REPORT_BATCH_EFFORT_ANALYSIS = "ReportBatchEffortAnalysis",
  REPORT_BATCH_PERFORMANCE = "ReportBatchPerformance",
  REPORT_BATCH_TEST_ANALYSIS = "ReportBatchTestAnalysis",
  REPORT_EFFORT_ANALYSIS = "ReportEffortAnalysis",
  REPORT_STUDENT_PROGRESS = "ReportStudentProgress",
  REPORT_SUBJECT_COVERAGE = "ReportSubjectCoverage",
  REPORT_SUBJECTIVE_QUESTIONS = "ReportSubjectiveQuestions",
  REPORT_SYLLABUS_COVERAGE = "ReportSyllabusCoverage",
  REVIEW_COURSE_CONTENT = "ReviewCourseContent",
  REVIEW_TAGGED_QUESTIONS = "ReviewTaggedQuestions",
  SCHOOL_IMPLEMENTATION_SCORE = "SchoolImplementationScore",
  SELF_PRACTICE = "SelfPractice",
  SELF_TESTS = "SelfTests",
  SIMILAR_QUESTIONS_REVIEW = "SimilarQuestionsReview",
  STUDENT_DASHBOARD = "StudentDashboard",
  STUDENT_MARKSHEET_REPORTS = "StudentMarksheetReports",
  STUDENT_PRELOADED_TESTS = "StudentPreloadedTests",
  STUDENT_PROFILE = "StudentProfile",
  TAG_NEW_QUESTIONS = "TagNewQuestions",
  TEACHING_CONTENT = "TeachingContent",
  TEACHER_DASHBOARD = "TeacherDashboard",
  TUTORING_ADMIN_DASHBOARD = "TutoringAdminDashboard",
  TUTORING_ADMIN_SESSION_REPORTS = "TutoringAdminSessionReports",
  TUTORING_BOOK_DEMO_SESSION = "TutoringBookDemoSession",
  TUTORING_BOOK_PAID_SESSIONS = "TutoringBookPaidSessions",
  TUTORING_MANAGE_DEMO_SESSIONS = "TutoringManageDemoSessions",
  TUTORING_MANAGE_DEMO_SLOTS = "TutoringManageDemoSlots",
  TUTORING_MANAGE_DEMO_STUDENTS = "TutoringManageDemoStudents",
  TUTORING_MANAGE_PAID_SESSIONS = "TutoringManagePaidSessions",
  TUTORING_MANAGE_PAID_STUDENTS = "TutoringManagePaidStudents",
  TUTORING_MANAGE_STUDENT = "TutoringManageStudent",
  TUTORING_MONTHLY_REPORT = "TutoringMonthlyReport",
  TUTORING_STUDENT_DASHBOARD = "TutoringStudentDashboard",
  TUTORING_STUDENT_STUDY_NOTES = "TutoringStudentStudyNotes",
  TUTORING_ONBOARD_STUDENT = "TutoringOnboardStudent",
  TUTORING_TUTOR_DASHBOARD = "TutoringTutorDashboard",
  TUTORING_TUTOR_LESSON_PLANS = "TutoringTutorLessonPlans",
}

export type NavbarViewName = NavbarParentNamesEnum | ViewNamesEnum;

// Display names for every role across the 9 per-group role-ID enums. Keys MUST mirror the ID-enum
// keys — getIdNamePairsFromMatchingEnums() pairs the two by key, and a role missing here silently
// drops out of every role picker. An explicit enum (not title-cased keys) because acronym roles
// would otherwise render as "Spoc" / "Kdm" / "B2c Student".
export enum UserRoleNamesEnum {
  // Student roles
  STUDENT = "Student",
  DEMO_STUDENT = "Demo Student",
  DEACTIVATED_STUDENT = "Deactivated Student",
  PARENT = "Parent",
  OLYMPIAD_STUDENT = "Olympiad Student",
  STEMLEARN_STUDENT = "STEMLearn Student",
  B2C_STUDENT = "B2C Student",
  // Affiliation admin roles
  CENTER_MANAGER = "Center Manager",
  B2C_INDIVIDUAL_TEACHER = "B2C Individual Teacher",
  PRINCIPAL = "Principal",
  KDM = "KDM",
  SPOC = "SPOC",
  CSM = "CSM",
  AFFILIATION_HEAD = "Affiliation Head",
  // Teacher roles
  SCHOOL_TEACHER = "School Teacher",
  // Internal admin roles
  ADMIN = "Admin",
  // Affiliation manager roles
  AFFILIATION_MANAGER = "Affiliation Manager",
  SCHOOL_PARTNER_RM = "School Partner RM",
  // Internal content roles
  CONTENT_MANAGER = "Content Manager",
  FACULTY = "Faculty",
  // Tutoring admin roles
  TUTORING_ADMIN = "Tutoring Admin",
  TUTORING_OPS = "Tutoring Ops",
  TUTORING_SALES = "Tutoring Sales",
  // Tutor roles
  TUTOR = "Tutor",
  // Tutoring student roles
  TUTORING_STUDENT = "Tutoring Student",
}
